import axios from "axios";
import React, { useEffect, useState } from "react";
import { api } from "../utils/Api";
import {
	Typography,
	Flex,
	Button,
	notification,
	Tag,
	Statistic,
	Table,
} from "antd";
import Layout from "../layout/Layout";
import { CustomSearch, CustomSpinner } from "../utils/CustomComponents";
import dayjs from "dayjs";
import {
	FilePdfOutlined,
	RetweetOutlined,
	RollbackOutlined,
} from "@ant-design/icons";
const { Text } = Typography;

const QAQCCustomerTestReports = ({ history }) => {
	const [loading, setLoading] = useState(true);
	const [loadingTable, setLoadingTable] = useState(false);
	const [jobList, setJobList] = useState([]);
	const [allJobs, setAllJobs] = useState([]);
	const [startDate, setStartDate] = useState(dayjs());
	const [endDate, setEndDate] = useState(dayjs());
	const [apiNotification, contextHolder] = notification.useNotification();

	const [refresh, setRefresh] = useState(false);

	const [customer, setCustomer] = useState("All");
	const [customerList, setCustomerList] = useState([]);
	const [customerLoading, setCustomerLoading] = useState(true);

	const [jobId, setJobId] = useState("All");
	const [jobIdList, setJobIdList] = useState([]);
	const [jobLoading, setJobLoading] = useState(true);

	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const [selectedJobs, setSelectedJobs] = useState([]);

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	/**
	 ** Options header (columns)
	 */
	const columns = [
		{
			title: "Date",
			dataIndex: "scheduledDate",
			key: "scheduledDate",
			sorter: (a, b) =>
				dayjs(a.scheduledDate).valueOf() - dayjs(b.scheduledDate).valueOf(),
			render: (value) => {
				if (!value || !dayjs(value).isValid()) return "-";
				return <Text>{dayjs(value).format("MM/DD/YYYY hh:mm A")}</Text>;
			},
		},
		{
			title: "Job ID",
			dataIndex: "number",
			key: "number",
			sorter: (a, b) => Number(a.number) - Number(b.number),
			render: (value) => {
				return <Text strong>{value}</Text>;
			},
		},
		{
			title: "Customer",
			dataIndex: "customer",
			key: "customer",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Seller",
			dataIndex: "seller",
			key: "seller",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Plant",
			dataIndex: "plant",
			key: "plant",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Loaded YD3",
			dataIndex: "loadYD3",
			key: "loadYD3",
			render: (value) => {
				return (
					<Statistic
						value={value}
						valueStyle={{
							fontSize: 14,
						}}
					/>
				);
			},
		},
		{
			title: "Target YD3",
			dataIndex: "targetYD3",
			key: "targetYD3",
			render: (value) => {
				return (
					<Statistic
						value={value}
						valueStyle={{
							fontSize: 14,
						}}
					/>
				);
			},
		},
		{
			title: "QC Type",
			dataIndex: "qcType",
			key: "qcType",
			render: (value) => {
				return (
					<Tag color={value === "Internal" ? "purple" : "yellow"}>{value}</Tag>
				);
			},
		},
		{
			title: "Franchise",
			dataIndex: "franchise",
			key: "franchise",
			render: (value) => {
				return (
					<Tag
						color={
							value === "57Concrete"
								? "blue"
								: value === "Castle"
									? "green"
									: "cyan"
						}
					>
						{value}
					</Tag>
				);
			},
		},
		{
			title: "Actions",
			dataIndex: "actions",
			key: "actions",
			render: (_, record) => {
				const isDisabled =
					!record.prePourComplete ||
					!record.pourTestingComplete ||
					!record.afterPourComplete ||
					!record.hasAllValuesOfEticket;

				const isDisabledEReportInternal = !record.hasLoadsWithTesting;
				const isDisabledEReportExternal = !record.hasUploadExternal;
				return (
					<Flex gap={10}>
						<Button
							type="primary"
							icon={<FilePdfOutlined />}
							disabled={isDisabled}
							onClick={() => {
								history.push(`/qc/job/fieldreports/pdf/${record.id}`);
							}}
						>
							Field Report
						</Button>
						{record.qcType === "Internal" && (
							<Button
								type="primary"
								onClick={() => {
									history.push(`/qc/job/load/laboratory/all/pdf/${record.id}`);
								}}
								disabled={isDisabledEReportInternal}
							>
								eReport
							</Button>
						)}
						{record.qcType === "External" && (
							<Button
								type="primary"
								onClick={() => generateLaboratoryOfExternal(record.id)}
								disabled={isDisabledEReportExternal}
							>
								eReport
							</Button>
						)}
					</Flex>
				);
			},
		},
	];

	const generateLaboratoryOfExternal = async (id) => {
		try {
			setLoadingTable(true);

			const url = api + `/job/generate/qc/uploadtexternal/${id}`;

			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const { data } = await axios.post(url);
			if (data.success) {
				const link = document.createElement("a");
				link.href = data.payload;
				link.download = "";
				document.body.appendChild(link);
				link.click();
				link.remove();
				openNotification("success", "Success", data.message);
			} else {
				openNotification("error", "Error", data.message);
			}
			setLoadingTable(false);
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", err.message);
			return null;
		}
	};

	useEffect(() => {
		getJobList();
		getJobIDList();
	}, [startDate, endDate, customer, refresh]);

	const getJobList = async () => {
		try {
			setLoadingTable(true);

			const start = `${startDate.year()}-${
				startDate.month() + 1
			}-${startDate.date()}`;
			const end = `${endDate.year()}-${endDate.month() + 1}-${endDate.date()}`;

			const url =
				api +
				`/job/get/qc/list/customertest?startDate=${start}&endDate=${end}&customer=${customer}`;

			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const { data } = await axios.get(url);
			if (data.success) {
				let list = [];
				data.payload.map((item, index) => {
					list.push({
						key: index,
						id: item._id,
						number: item.number,
						qcType: item.qcType ?? "-",
						franchise: item.franchise || "57Concrete",
						customer: item.customer.name,
						seller: item.seller.name,
						scheduledDate: item.scheduledDate,
						loadYD3: item.loadYD3,
						targetYD3: item.targetYD3,
						plant: item.plant.name,
						prePourComplete: item.qcData ? item.qcData.prePourComplete : false,
						pourTestingComplete: item.qcData
							? item.qcData.pourTestingComplete
							: false,
						afterPourComplete: item.qcData
							? item.qcData.afterPourComplete
							: false,
						hasAllValuesOfEticket: item.hasAllValuesOfEticket,
						hasLoadsWithTesting: item.hasLoadsWithTesting,
						hasUploadExternal: item.hasUploadExternal,
					});
				});
				setAllJobs(list);
				setJobList(list);
			} else {
				openNotification("error", "Error", data.message);
			}
			setLoadingTable(false);
			setLoading(false);
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", err.message);
			return null;
		}
	};

	const getJobIDList = async () => {
		try {
			setJobLoading(true);

			const start = `${startDate.year()}-${
				startDate.month() + 1
			}-${startDate.date()}`;
			const end = `${endDate.year()}-${endDate.month() + 1}-${endDate.date()}`;

			const url =
				api +
				`/job/get/qc/list/customertest/ids?startDate=${start}&endDate=${end}&customer=${customer}`;

			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const { data } = await axios.get(url);
			if (data.success) {
				let list = [
					{
						value: "All",
						label: "All",
					},
				];
				data.payload.map((item) => {
					list.push({
						label: item.number.toString(),
						value: item._id,
					});
				});
				setJobIdList(list);
			} else {
				openNotification("error", "Error", data.message);
			}
			setJobLoading(false);
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", err.message);
			return null;
		}
	};

	useEffect(() => {
		if (jobId === "All") {
			setJobList(allJobs);
		} else {
			const filtered = allJobs.filter((job) => job.id === jobId);
			setJobList(filtered);
		}
	}, [jobId, allJobs]);

	useEffect(() => {
		setJobId("All");
	}, [customer]);

	useEffect(() => {
		getCustomers();
	}, []);

	const getCustomers = async () => {
		try {
			const url = api + "/customer/list/options/57Concrete";
			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const { data } = await axios.get(url);
			if (data.success) {
				let list = [
					{
						value: "All",
						label: "All",
					},
				];
				data.payload.map((item) => {
					list.push({
						label: `${item.customerNumber} ${item.name}`,
						value: item._id,
					});
				});
				setCustomerList(list);
			} else {
				openNotification("error", "Error", data.message);
			}
			setCustomerLoading(false);
		} catch (err) {
			setCustomerLoading(false);
			openNotification("error", "Error", "Error while trying to get customers");
			console.error(err.message);
			return null;
		}
	};

	useEffect(() => {
		setSelectedRowKeys([]);
		setSelectedJobs([]);
	}, [jobList]);

	const rowSelection = {
		selectedRowKeys,
		onChange: (newSelectedRowKeys, selectedRows) => {
			setSelectedRowKeys(newSelectedRowKeys);
			setSelectedJobs(selectedRows.map((job) => job.id));
		},
	};

	const generateLaboratoryZipForJobs = async () => {
		try {
			setLoadingTable(true);

			const url = api + `/job/generate/qc/laboratory/zip`;

			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const { data } = await axios.post(url, { jobIds: selectedJobs });
			if (data.success) {
				const link = document.createElement("a");
				link.href = data.payload;
				link.download = "";
				document.body.appendChild(link);
				link.click();
				link.remove();
				openNotification("success", "Success", data.message);
			} else {
				openNotification("error", "Error", data.message);
			}
			setLoadingTable(false);
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", err.message);
			return null;
		}
	};

	const generateFieldReportZipForJobs = async () => {
		try {
			setLoadingTable(true);

			const url = api + `/job/generate/qc/fieldreport/zip`;

			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const { data } = await axios.post(url, { jobIds: selectedJobs });
			if (data.success) {
				const link = document.createElement("a");
				link.href = data.payload;
				link.download = "";
				document.body.appendChild(link);
				link.click();
				link.remove();
				if (data.message.includes("ambient conditions")) {
					openNotification("warning", "Warning", data.message);
				} else {
					openNotification("success", "Success", data.message);
				}
			} else {
				openNotification("error", "Error", data.message);
			}
			setLoadingTable(false);
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", err.message);
			return null;
		}
	};

	const RenderTable = () => {
		return (
			<Flex vertical gap={10}>
				<Table
					columns={columns}
					dataSource={jobList}
					loading={loading || loadingTable}
					rowSelection={rowSelection}
					pagination={{
						position: ["bottomRight"],
						pageSizeOptions: [10, 20, 50, 100, 250, 500],
						defaultPageSize: 10,
						showSizeChanger: true,
					}}
					scroll={{
						x: 1300,
					}}
					style={{
						minWidth: "100%",
					}}
				/>
				<Flex gap={10}>
					<Button
						type="primary"
						size="large"
						disabled={selectedJobs.length === 0 || loadingTable}
						loading={loadingTable}
						onClick={generateFieldReportZipForJobs}
					>
						Field Report Zip
					</Button>
					<Button
						type="primary"
						size="large"
						disabled={selectedJobs.length === 0 || loadingTable}
						loading={loadingTable}
						onClick={generateLaboratoryZipForJobs}
					>
						eReport Zip
					</Button>
				</Flex>
			</Flex>
		);
	};

	const onRangeChange = (dates) => {
		if (dates) {
			setStartDate(dates[0]);
			setEndDate(dates[1]);
		}
	};

	const RenderSearch = () => (
		<Flex gap={10} align="end" justify="space-between">
			<CustomSearch
				loading={loading}
				startDate={startDate}
				endDate={endDate}
				onRangeChange={onRangeChange}
				dateFormat={"MM/DD/YYYY"}
				labelPicker2={"Select customer"}
				pickerValue2={customer}
				optionsPicker2={customerList}
				onChangePicker2={(v) => setCustomer(v)}
				allowClearPicker2={false}
				pickerWidth2={250}
				disabledPicker={customerLoading}
				labelPicker3={"Select Job ID"}
				pickerValue3={jobId}
				optionsPicker3={jobIdList}
				onChangePicker3={(v) => setJobId(v)}
				allowClearPicker3={false}
				pickerWidth3={250}
				disabledPicker3={jobLoading}
				pickerTitle={"Has Testing Loads?"}
				pickerTitle2={"Customer"}
				pickerTitle3={"Job ID"}
			/>
			<Button
				type="primary"
				onClick={() => setRefresh((prev) => !prev)}
				icon={<RetweetOutlined />}
				size="large"
				loading={loading}
			/>
		</Flex>
	);

	const goBack = () => {
		history.goBack();
	};

	return (
		<Layout title="Customer Test Reports" description="Report">
			<Flex vertical gap={16}>
				<Button
					type="primary"
					size="large"
					icon={<RollbackOutlined />}
					onClick={goBack}
					loading={loading}
					disabled={loading}
				/>
				<RenderSearch />
				{loading ? <CustomSpinner /> : <RenderTable />}
				{contextHolder}
			</Flex>
		</Layout>
	);
};

export default QAQCCustomerTestReports;
